/**
 * The CRM modules that mirror Zoho CRM (imported by the API's zoho:import-*
 * commands). One list page + one detail page serve them all; this says what
 * each shows. Field keys are the API's column names (CrmMirrorResources).
 */
export type FieldType = 'text' | 'textarea' | 'number' | 'money' | 'date' | 'datetime' | 'bool' | 'badge' | 'record';

export interface MirrorField {
  key: string;
  label: string;
  type?: FieldType;
  /** Shown on the detail page but not editable (imported values). */
  readonly?: boolean;
}

export interface MirrorChild {
  /** Relation key on the detail response, e.g. "items". */
  key: string;
  label: string;
  columns: MirrorField[];
  /** Rows are saved with the record (replace-all) - otherwise read-only. */
  editable?: boolean;
  /** Each row opens another mirror module's detail page. */
  link?: string;
}

export interface MirrorModule {
  key: string;
  title: string;
  singular: string;
  icon: string;
  blurb: string;
  /** Field shown as the record's name. */
  nameKey: string;
  columns: MirrorField[];
  filters: { key: string; label: string; options?: { value: string; label: string }[] }[];
  fields: MirrorField[];
  children?: MirrorChild[];
  /** A single related record shown on the detail page, e.g. an ad's ad set. */
  parents?: { key: string; label: string; link: string; nameKey: string }[];
  /** Defaults for a new record. */
  defaults?: Record<string, any>;
}

const RECORD: MirrorField = { key: 'record_name', label: 'CRM record', type: 'record' };
const OWNER: MirrorField = { key: 'owner_name', label: 'Zoho owner', readonly: true };

export const MIRROR_MODULES: MirrorModule[] = [
  {
    key: 'invoices', title: 'Invoices', singular: 'Invoice', icon: 'bi-receipt', nameKey: 'subject',
    blurb: 'Customer invoices brought across from Zoho CRM, with their line items.',
    columns: [
      { key: 'invoice_no', label: 'No.' }, { key: 'subject', label: 'Subject' }, RECORD,
      { key: 'invoice_date', label: 'Date', type: 'date' }, { key: 'due_date', label: 'Due', type: 'date' },
      { key: 'grand_total', label: 'Total', type: 'money' }, { key: 'amount_paid', label: 'Paid', type: 'money' },
      { key: 'status', label: 'Status', type: 'badge' }
    ],
    filters: [{ key: 'status', label: 'Status' }],
    fields: [
      { key: 'subject', label: 'Subject' }, { key: 'invoice_no', label: 'Invoice no.' }, { key: 'status', label: 'Status' },
      { key: 'invoice_date', label: 'Invoice date', type: 'date' }, { key: 'due_date', label: 'Due date', type: 'date' },
      { key: 'sub_total', label: 'Sub total', type: 'money' }, { key: 'tax_total', label: 'Tax', type: 'money' },
      { key: 'grand_total', label: 'Grand total', type: 'money' }, { key: 'amount_paid', label: 'Amount paid', type: 'money' },
      { key: 'date_of_payment', label: 'Date of payment', type: 'date' },
      { key: 'account_name', label: 'Zoho account' }, { key: 'contact_name', label: 'Zoho contact' }, RECORD, OWNER
    ],
    children: [{
      key: 'items', label: 'Line items', editable: true, columns: [
        { key: 'product_name', label: 'Product' }, { key: 'description', label: 'Description', type: 'textarea' },
        { key: 'quantity', label: 'Qty', type: 'number' }, { key: 'list_price', label: 'Price', type: 'money' },
        { key: 'discount', label: 'Discount', type: 'money' }, { key: 'total', label: 'Total', type: 'money' },
        { key: 'tax', label: 'Tax', type: 'money' }, { key: 'net_total', label: 'Net total', type: 'money' }
      ]
    }],
    defaults: { status: 'Draft' }
  },
  {
    key: 'quotes', title: 'Quotes', singular: 'Quote', icon: 'bi-calculator', nameKey: 'product_name',
    blurb: 'DEDI and Flexi quoting from Zoho deals, and quotes on Zoho leads.',
    columns: [
      { key: 'kind', label: 'Type', type: 'badge' }, { key: 'parent_name', label: 'Deal / lead' }, RECORD,
      { key: 'product_name', label: 'Product' }, { key: 'category', label: 'Category' },
      { key: 'base_rate', label: 'Base rate', type: 'money' }, { key: 'fuel_surcharge', label: 'Fuel %', type: 'number' },
      { key: 'adjusted_rate', label: 'Adjusted rate', type: 'money' }
    ],
    filters: [
      { key: 'kind', label: 'Type', options: [{ value: 'dedi', label: 'DEDI' }, { value: 'flexi', label: 'Flexi' }, { value: 'lead', label: 'Lead quote' }] },
      { key: 'category', label: 'Category' }
    ],
    fields: [
      { key: 'kind', label: 'Type' }, { key: 'parent_name', label: 'Deal / lead' }, { key: 'product_name', label: 'Product' },
      { key: 'category', label: 'Category' }, { key: 'specs', label: 'Specs' }, { key: 'pallet_spaces', label: 'Pallet spaces' },
      { key: 'base_rate', label: 'Base rate (incl. fuel)', type: 'money' }, { key: 'fuel_surcharge', label: 'Fuel surcharge %', type: 'number' },
      { key: 'fee', label: 'Fee', type: 'money' }, { key: 'adjusted_rate', label: 'Adjusted rate', type: 'money' }, RECORD, OWNER
    ],
    defaults: { kind: 'dedi' }
  },
  {
    key: 'products', title: 'Products', singular: 'Product', icon: 'bi-box', nameKey: 'name',
    blurb: 'Vehicle products and their DEDI / Flexi rates, used on quotes and invoices.',
    columns: [
      { key: 'name', label: 'Name' }, { key: 'code', label: 'Code' }, { key: 'category', label: 'Category' },
      { key: 'pallet_spaces', label: 'Pallets' }, { key: 'unit_price', label: 'Unit price', type: 'money' },
      { key: 'dedi_marathon', label: 'DEDI Marathon', type: 'money' }, { key: 'dedi_sprint', label: 'DEDI Sprint', type: 'money' },
      { key: 'flexi', label: 'Flexi', type: 'money' }, { key: 'active', label: 'Active', type: 'bool' }
    ],
    filters: [{ key: 'category', label: 'Category' }],
    fields: [
      { key: 'name', label: 'Name' }, { key: 'code', label: 'Code' }, { key: 'category', label: 'Category' },
      { key: 'description', label: 'Description', type: 'textarea' }, { key: 'specs', label: 'Specs' }, { key: 'pallet_spaces', label: 'Pallet spaces' },
      { key: 'active', label: 'Active', type: 'bool' }, { key: 'taxable', label: 'Taxable', type: 'bool' }, { key: 'tax', label: 'Tax' },
      { key: 'unit_price', label: 'Unit price', type: 'money' }, { key: 'fuel_surcharge', label: 'Fuel surcharge %', type: 'number' },
      { key: 'dedi_marathon', label: 'DEDI Marathon', type: 'money' }, { key: 'dedi_sprint', label: 'DEDI Sprint', type: 'money' },
      { key: 'flexi', label: 'Flexi', type: 'money' }, { key: 'flexi_load_guarantee_4', label: 'Flexi load guarantee 4', type: 'money' },
      { key: 'flexi_load_guarantee_60', label: 'Flexi load guarantee 60', type: 'money' },
      { key: 'flexi_load_guarantee_100', label: 'Flexi load guarantee 100', type: 'money' }, OWNER
    ],
    defaults: { active: true, taxable: true }
  },
  {
    key: 'signed-documents', title: 'Signed documents', singular: 'Signed document', icon: 'bi-pen', nameKey: 'name',
    blurb: 'Documents sent for signature through ZohoSign, with recipients and history.',
    columns: [
      { key: 'name', label: 'Document' }, RECORD, { key: 'contact_name', label: 'Contact' },
      { key: 'date_sent', label: 'Sent', type: 'date' }, { key: 'date_completed', label: 'Completed', type: 'date' },
      { key: 'status', label: 'Status', type: 'badge' }
    ],
    filters: [{ key: 'status', label: 'Status' }],
    fields: [
      { key: 'name', label: 'Document' }, { key: 'status', label: 'Status' },
      { key: 'date_sent', label: 'Date sent', type: 'date' }, { key: 'deadline', label: 'Deadline', type: 'date' },
      { key: 'date_completed', label: 'Date completed', type: 'date' }, { key: 'date_declined', label: 'Date declined', type: 'date' },
      { key: 'declined_reason', label: 'Declined reason', type: 'textarea' }, { key: 'note', label: 'Note', type: 'textarea' },
      { key: 'deal_name', label: 'Zoho deal', readonly: true }, { key: 'account_name', label: 'Zoho account' },
      { key: 'contact_name', label: 'Zoho contact' }, { key: 'zohosign_document_id', label: 'ZohoSign document id', readonly: true }, RECORD, OWNER
    ],
    children: [
      { key: 'recipients', label: 'Recipients', columns: [
        { key: 'signing_order', label: '#' }, { key: 'name', label: 'Name' }, { key: 'email', label: 'Email' }, { key: 'type', label: 'Action' },
        { key: 'status', label: 'Status', type: 'badge' }, { key: 'date_delivered', label: 'Delivered', type: 'date' },
        { key: 'date_completed', label: 'Completed', type: 'date' }, { key: 'declined_reason', label: 'Declined reason' }
      ] },
      { key: 'events', label: 'History', columns: [
        { key: 'event_date', label: 'Date', type: 'date' }, { key: 'description', label: 'Event' }
      ] }
    ],
    defaults: { status: 'Drafted' }
  },
  {
    key: 'campaigns', title: 'Campaigns', singular: 'Campaign', icon: 'bi-megaphone', nameKey: 'name',
    blurb: 'Email, SMS and Facebook campaigns from Zoho CRM.',
    columns: [
      { key: 'name', label: 'Campaign' }, { key: 'type', label: 'Type' }, { key: 'status', label: 'Status', type: 'badge' },
      { key: 'replies', label: 'Replies', type: 'number' }, { key: 'zoho_created_at', label: 'Created', type: 'datetime' }
    ],
    filters: [{ key: 'type', label: 'Type' }, { key: 'status', label: 'Status' }],
    fields: [
      { key: 'name', label: 'Campaign' }, { key: 'type', label: 'Type' }, { key: 'status', label: 'Status' },
      { key: 'email_subject', label: 'Email subject' }, { key: 'sender_name', label: 'Sender name' },
      { key: 'sender_address', label: 'Sender address' }, { key: 'reply_to_address', label: 'Reply-to address' },
      { key: 'replies', label: 'SMS replies', readonly: true }, { key: 'actual_cost', label: 'Actual cost', type: 'money' },
      { key: 'starts_at', label: 'Starts', type: 'datetime' }, { key: 'ends_at', label: 'Ends', type: 'datetime' },
      { key: 'fb_objective', label: 'Facebook objective', readonly: true }, { key: 'fb_status', label: 'Facebook status', readonly: true },
      { key: 'fb_buying_type', label: 'Facebook buying type', readonly: true }, { key: 'fb_account_id', label: 'Facebook account', readonly: true },
      { key: 'zoho_created_at', label: 'Created in Zoho', type: 'datetime', readonly: true }, OWNER
    ],
    children: [{ key: 'adsets', label: 'Facebook ad sets', link: 'facebook-adsets', columns: [
      { key: 'name', label: 'Ad set' }, { key: 'effective_status', label: 'Status', type: 'badge' },
      { key: 'optimization_goal', label: 'Goal' }, { key: 'starts_at', label: 'Starts', type: 'datetime' }, { key: 'ends_at', label: 'Ends', type: 'datetime' }
    ] }]
  },
  {
    key: 'facebook-adsets', title: 'Facebook ad sets', singular: 'Ad set', icon: 'bi-collection', nameKey: 'name',
    blurb: 'Facebook ad sets synced into Zoho by the Facebook Advert Manager.',
    columns: [
      { key: 'name', label: 'Ad set' }, { key: 'status', label: 'Status', type: 'badge' }, { key: 'effective_status', label: 'Effective status' },
      { key: 'optimization_goal', label: 'Goal' }, { key: 'lifetime_budget', label: 'Lifetime budget', type: 'number' },
      { key: 'starts_at', label: 'Starts', type: 'datetime' }
    ],
    filters: [{ key: 'status', label: 'Status' }, { key: 'effective_status', label: 'Effective status' }],
    fields: [
      { key: 'name', label: 'Ad set' }, { key: 'status', label: 'Status' }, { key: 'effective_status', label: 'Effective status' },
      { key: 'billing_event', label: 'Billing event' }, { key: 'optimization_goal', label: 'Optimisation goal' },
      { key: 'daily_budget', label: 'Daily budget (cents)', type: 'number' }, { key: 'lifetime_budget', label: 'Lifetime budget (cents)', type: 'number' },
      { key: 'budget_remaining', label: 'Budget remaining', readonly: true }, { key: 'starts_at', label: 'Starts', type: 'datetime' },
      { key: 'ends_at', label: 'Ends', type: 'datetime' }, { key: 'fb_adset_id', label: 'Facebook ad set id', readonly: true }, OWNER
    ],
    parents: [{ key: 'campaign', label: 'Campaign', link: 'campaigns', nameKey: 'name' }],
    children: [{ key: 'ads', label: 'Ads', link: 'facebook-ads', columns: [
      { key: 'name', label: 'Ad' }, { key: 'configured_status', label: 'Status', type: 'badge' }, { key: 'fb_ad_id', label: 'Facebook ad id' }
    ] }]
  },
  {
    key: 'facebook-ads', title: 'Facebook ads', singular: 'Ad', icon: 'bi-badge-ad', nameKey: 'name',
    blurb: 'Facebook ads synced into Zoho by the Facebook Advert Manager.',
    columns: [{ key: 'name', label: 'Ad' }, { key: 'configured_status', label: 'Status', type: 'badge' }, { key: 'fb_ad_id', label: 'Facebook ad id' }],
    filters: [{ key: 'configured_status', label: 'Status' }],
    fields: [
      { key: 'name', label: 'Ad' }, { key: 'configured_status', label: 'Status' }, { key: 'bid_type', label: 'Bid type' },
      { key: 'bid_amount', label: 'Bid amount', type: 'number' }, { key: 'fb_ad_id', label: 'Facebook ad id', readonly: true },
      { key: 'fb_account_id', label: 'Facebook account', readonly: true }, OWNER
    ],
    parents: [{ key: 'adset', label: 'Ad set', link: 'facebook-adsets', nameKey: 'name' }]
  },
  {
    key: 'account-plans', title: 'Account plans', singular: 'Account plan', icon: 'bi-clipboard2-data', nameKey: 'name',
    blurb: 'Zoho Account Management records and their 90 day plans.',
    columns: [{ key: 'name', label: 'Name' }, { key: 'owner_name', label: 'Zoho owner' }, { key: 'created_at', label: 'Created', type: 'datetime' }],
    filters: [],
    fields: [{ key: 'name', label: 'Name' }, OWNER],
    children: [{ key: 'tasks', label: '90 day plan', editable: true, columns: [
      { key: 'task', label: 'Task' }, { key: 'status', label: 'Status' }, { key: 'due_date', label: 'Due', type: 'date' },
      { key: 'assigned_to_name', label: 'Assigned to' }, { key: 'record_name', label: 'Depot', type: 'record' }
    ] }]
  },
  {
    key: 'competitor-shares', title: 'Competitor shares', singular: 'Competitor share', icon: 'bi-pie-chart', nameKey: 'competitor',
    blurb: 'Competitors\' market share recorded on Zoho depot health checks.',
    columns: [
      { key: 'record_name', label: 'Depot', type: 'record' }, { key: 'competitor', label: 'Competitor' },
      { key: 'market_share', label: 'Market share %', type: 'number' }, { key: 'competitor_market_share', label: 'Competitor share %', type: 'number' },
      { key: 'health_check_date', label: 'Health check', type: 'date' }
    ],
    filters: [{ key: 'competitor', label: 'Competitor' }],
    fields: [
      { key: 'competitor', label: 'Competitor' }, { key: 'market_share', label: 'Market share %', type: 'number' },
      { key: 'competitor_market_share', label: 'Competitor share %', type: 'number' },
      { key: 'health_check_name', label: 'Health check' }, { key: 'health_check_date', label: 'Health check date', type: 'date' },
      { key: 'record_name', label: 'Depot', type: 'record' }, OWNER
    ]
  },
  {
    key: 'regions', title: 'Regions', singular: 'Region', icon: 'bi-geo-alt', nameKey: 'name',
    blurb: 'Sales regions and the postcodes they cover.',
    columns: [{ key: 'name', label: 'Region' }, { key: 'postcodes', label: 'Postcodes' }],
    filters: [],
    fields: [{ key: 'name', label: 'Region' }, { key: 'postcodes', label: 'Postcodes', type: 'textarea' }, OWNER]
  }
];

export function mirrorModule (key: string): MirrorModule | undefined {
  return MIRROR_MODULES.find(m => m.key === key);
}
